<template>
  <div class="store-settings">
    <div class="store-settings-intro mb-4">
      <div class="text-h6 font-weight-medium">
        {{ $t("store_settings") }}
      </div>
      <div class="text-body-2 grey--text text--darken-1 mt-1">
        {{ $t("store_settings_intro_hint") }}
      </div>
    </div>

    <v-tabs
      v-model="storeSettingsTab"
      show-arrows
      background-color="transparent"
      class="store-settings-tabs mb-2"
      slider-color="primary"
    >
      <v-tab>{{ $t("profile") }}</v-tab>
      <v-tab>
         {{ $t("payment_and_vat") }}
        <v-chip v-if="pmOnCount" x-small class="ml-2">{{ pmOnCount }}</v-chip>
      </v-tab>
      <v-tab>{{ $t("features") }}</v-tab>
      <v-tab>{{ $t("customer") }}</v-tab>
    </v-tabs>

    <v-tabs-items v-model="storeSettingsTab" class="store-settings-tab-items">
      <!-- Profile -->
      <v-tab-item>
        <v-fade-transition hide-on-leave>
          <div class="store-settings-tab-panel py-2">
            <!-- Store Identity Section -->
            <SettingsSectionCard
              icon="fal fa-store"
              :title="$t('store_identity')"
              :subtitle="$t('store_identity_hint')"
              accent
              class="mb-6"
            >
              <v-row align="center">
                <!-- Store Logo -->
                <v-col cols="12" md="4" class="text-center">
                  <v-badge
                    bordered
                    bottom
                    color="primary"
                    class="avatar-camera-badge"
                    icon="fas fa-camera"
                    overlap
                  >
                    <v-avatar
                      color="#f5f5f5"
                      size="158"
                      v-if="MyRole('change_logo_store')"
                    >
                      <v-avatar-uploader
                        :url="urlLogo"
                        :clickable="true"
                        field="file"
                        accept="image/jpeg,image/png,image/gif,image/svg+xml"
                        :rename="rename"
                        @failed="failed"
                        :request="requestLogo"
                      />
                    </v-avatar>
                    <v-avatar size="150" v-else>
                      <v-img :src="urlLogo"></v-img>
                    </v-avatar>
                  </v-badge>
                  <div class="text-caption grey--text mt-2">
                    {{ $t("click_to_change_logo") }}
                  </div>
                </v-col>

                <v-divider vertical class="hidden-sm-and-down" />

                <!-- Store Info -->
                <v-col cols="12" md="8">
                  <div class="store-info-grid">
                    <div class="info-row">
                      <span class="info-label">{{ $t("store_name") }}</span>
                      <span class="info-value">{{ user?.stores?.name }}</span>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="info-row">
                      <span class="info-label">{{ $t("store_code") }}</span>
                      <span class="info-value">{{ user?.stores?.code }}</span>
                    </div>
                    <v-divider class="my-2"></v-divider>

                    <div class="info-row">
                      <span class="info-label">{{ $t("business_type") }}</span>
                      <v-chip
                        small
                        label
                        color="primary"
                        class="text-capitalize rounded-lg"
                      >
                        {{ user?.stores?.store_type_code }}
                      </v-chip>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="info-row">
                      <span class="info-label">{{ $t("cost_method") }}</span>
                      <span class="info-value">
                        Weighted Average Cost
                        <v-btn
                          icon
                          x-small
                          class="ml-1"
                          @click="showDetailedCalculation = true"
                        >
                          <v-icon x-small color="primary"
                            >fas fa-info-circle</v-icon
                          >
                        </v-btn>
                      </span>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="info-row">
                      <span class="info-label">{{
                        $t("subscription_ends")
                      }}</span>
                      <v-chip
                        small
                        color="primary"
                        text-color="white"
                        class="rounded-lg"
                      >
                        {{ user?.stores?.end_date }}
                      </v-chip>
                    </div>
                    
                  </div>
                </v-col>
              </v-row>
            </SettingsSectionCard>

            <!-- Contact Section -->
            <SettingsSectionCard
              icon="fal fa-address-book"
              :title="$t('store_information')"
              :subtitle="$t('basic_store_details')"
              accent
              class="mb-6"
            >
              <v-row>
                <v-col cols="12" md="6">
                  <TTextField
                    label="store_name"
                    v-model="store.name"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <TTextField
                    label="mobile_no"
                    v-model="store.mobile_no"
                    :maxlength="20"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <TTextField
                    label="store_phone"
                    v-model="store.phone_no"
                    :maxlength="20"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <TTextField
                    label="store_address"
                    v-model="store.address"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12">
                  <TextArea
                    label="description"
                    v-model="store.desc"
                    outlined
                    :rows="2"
                  />
                </v-col>
              </v-row>
            </SettingsSectionCard>

            <!-- Store Cover Section -->
            <SettingsSectionCard
              v-if="MyRole('change_logo_store')"
              icon="fal fa-image"
              :title="$t('store_cover')"
              :subtitle="$t('store_cover_hint')"
              accent
              class="mb-6"
            >
              <DragDropUpload
                v-model="coverFile"
                accept="image/*"
                :max-size-mb="5"
                @file-selected="uploadCover"
              />
            </SettingsSectionCard>

            <!-- Plan Section -->
            <SettingsSectionCard
              icon="fal fa-layer-group"
              :title="$t('plan')"
              accent
              class="mb-6"
            >
              <template v-slot:actions>
                <v-chip
                  small
                  label
                  color="primary"
                  text-color="white"
                  class="rounded-lg"
                >
                  {{ planTag }}
                </v-chip>
              </template>

              <v-row dense>
                <v-col
                  v-for="stat in planStats"
                  :key="stat.label"
                  cols="6"
                  md="3"
                >
                  <div class="plan-stat-tile">
                    <div class="plan-stat-tile__label">
                      {{ $t(stat.label) }}
                    </div>
                    <div class="plan-stat-tile__value">
                      <template v-if="stat.isDate">{{ stat.value }}</template>
                      <template v-else>{{ stat.value | ToNumber }}</template>
                    </div>
                    <div class="plan-stat-tile__note">{{ $t(stat.note) }}</div>
                  </div>
                </v-col>
              </v-row>

              <div class="mt-4">
                <div class="d-flex align-baseline">
                  <span class="text-caption grey--text">{{
                    user?.stores?.start_date
                  }}</span>
                  <v-spacer />
                  <span class="text-caption grey--text">{{
                    user?.stores?.end_date
                  }}</span>
                </div>
                <v-progress-linear
                  :value="planProgressPct"
                  height="6"
                  rounded
                  color="primary"
                  background-color="grey lighten-3"
                  class="my-1"
                />
                <span class="text-caption grey--text"
                  >{{ planDaysLeft }} {{ $t("days_remaining") }}</span
                >
              </div>
            </SettingsSectionCard>
          </div>
        </v-fade-transition>
      </v-tab-item>

       <!-- Payment & VAT -->
      <v-tab-item>
        <v-fade-transition hide-on-leave>
          <div class="store-settings-tab-panel py-2">
             <!-- VAT Section -->
            <SettingsSectionCard
              icon="fal fa-percent"
              title="VAT (10%)"
              accent
              class="mb-6"
            >
              <v-list-item class="settings-toggle-row">
                <v-list-item-icon class="mr-3">
                  <v-sheet
                    color="grey lighten-4"
                    rounded="lg"
                    class="d-flex align-center justify-center"
                    width="36"
                    height="36"
                  >
                    <v-icon color="primary" small>fas fa-percent</v-icon>
                  </v-sheet>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ $t("vat_included") }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ $t("vat_included_hint") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-switch
                    v-model="store.tax_include"
                    inset
                    hide-details
                    color="primary"
                    class="mt-0"
                  />
                </v-list-item-action>
              </v-list-item>
            </SettingsSectionCard>

            <!-- QR Code & Payment Section -->
            <SettingsSectionCard
              icon="fal fa-qrcode"
              :title="$t('bank_qr')"
              :subtitle="$t('qr_code_hint')"
              accent
              class="mb-6"
              body-class="pa-0"
            >
              <!-- Dynamic QR Payment Toggle -->
              <v-list-item>
                <v-list-item-icon class="mr-3">
                  <v-sheet
                    color="grey lighten-4"
                    rounded="lg"
                    class="d-flex align-center justify-center"
                    width="36"
                    height="36"
                  >
                    <v-icon color="primary" small>fas fa-qrcode</v-icon>
                  </v-sheet>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ $t("dynamic_qr_payment") }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ $t("dynamic_qr_payment_hint") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-switch
                    v-model="store.is_active_qr"
                    inset
                    hide-details
                    color="primary"
                    class="mt-0"
                  />
                </v-list-item-action>
              </v-list-item>
            </SettingsSectionCard>

            <!-- Payment Methods Section -->
            <SettingsSectionCard
              icon="fal fa-credit-card"
              :title="$t('payment_methods_settings')"
              :subtitle="$t('payment_methods_hint')"
              accent
              class="mb-6"
              body-class="pa-0"
            >
              <v-list class="py-0">
                <v-list-item
                  v-for="(method, index) in paymentMethodsList"
                  :key="method.key"
                  :class="{
                    'border-bottom': index < paymentMethodsList.length - 1,
                  }"
                  :disabled="method.disabled"
                >
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>{{ method.icon }}</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t(method.label) }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t(method.hint) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="paymentMethods[method.key]"
                      inset
                      hide-details
                      color="primary"
                      :disabled="method.disabled"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>

              <!-- Transfer Static QR Code Upload Section -->
              <v-expand-transition>
                <div
                  v-if="paymentMethods.transfer"
                  class="pa-4 qr-upload-section border-top"
                >
                  <div class="text-subtitle-2 font-weight-medium mb-3">
                    <v-icon color="blue" small left>fas fa-qrcode</v-icon>
                    {{ $t("transfer_static_qr_code") }}
                  </div>
                  <div class="text-caption qr-description-text mb-3">
                    {{ $t("transfer_static_qr_description") }}
                  </div>
                  <div class="d-flex justify-center">
                    <div class="qr-upload-container">
                      <v-avatar class="qr-avatar-bg rounded-lg" size="140" tile>
                        <v-avatar-uploader
                          :url="urlQR"
                          :tile="true"
                          :clickable="true"
                          field="file"
                          :rename="rename"
                          @failed="failed"
                          :request="requestQR"
                        />
                      </v-avatar>
                      <div class="qr-actions mt-2 text-center">
                        <v-btn
                          v-if="is_qr"
                          @click="conRemove()"
                          text
                          x-small
                          color="error"
                        >
                          <v-icon x-small left>fas fa-trash</v-icon>
                          {{ $t("remove") }}
                        </v-btn>
                        <div v-else class="text-caption qr-upload-hint">
                          {{ $t("click_to_upload") }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </v-expand-transition>
            </SettingsSectionCard>

            <!-- Rounding & Shift Section -->
            <SettingsSectionCard
              icon="fal fa-sliders-h"
              :title="$t('rounding_shift')"
              accent
              class="mb-6"
              body-class="pa-0"
            >
              <v-list class="py-0">
                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small
                        >fas fa-balance-scale</v-icon
                      >
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("round_up_totals") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("round_up_totals_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.round_up"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-clock</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("require_shift") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("require_shift_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.require_shift"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item>
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-receipt</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("show_payment_method") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("show_payment_method_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.show_payment_method"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </SettingsSectionCard>
          </div>
        </v-fade-transition>
      </v-tab-item>

      <!-- Receipt settings are available from the sidebar Receipt page. -->
      <v-tab-item v-if="false">
        <v-fade-transition hide-on-leave>
          <div class="store-settings-tab-panel py-2">
            <v-row>
              <v-col cols="12" md="7">
                <!-- Format Section -->
                <SettingsSectionCard
                  icon="fal fa-receipt"
                  :title="$t('format')"
                  accent
                  class="mb-6"
                >
                  <div class="text-caption font-weight-medium mb-2">
                    {{ $t("receipt_size") }}
                  </div>
                  <v-chip-group
                    v-model="receiptSize"
                    mandatory
                    active-class="primary--text"
                    class="mb-2"
                  >
                    <v-chip
                      v-for="size in receiptSizeOptions"
                      :key="size"
                      :value="size"
                      filter
                      outlined
                      class="rounded-lg"
                    >
                      {{ size }}
                    </v-chip>
                  </v-chip-group>

                  <template v-if="hasKitchenReceiptSettings">
                    <div class="text-caption font-weight-medium mt-4 mb-2">
                      {{ $t("kitchen_receipt_language") }}
                    </div>
                    <TextSelect
                      v-model="store.kitchen_receipt_language_mode"
                      :items="kitchenReceiptLanguageOptions"
                      text="label"
                      itemValue="value"
                      :placeholder="false"
                      dense
                    />
                    <div class="text-caption grey--text mt-2">
                      {{ $t("kitchen_receipt_language_hint") }}
                    </div>

                    <div class="kitchen-receipt-preview mt-3 pa-3 rounded-lg">
                      <div
                        class="text-caption grey--text mb-2 d-flex align-center"
                      >
                        <v-icon x-small class="mr-1">fas fa-eye</v-icon>
                        {{ $t("preview") }}
                      </div>
                      <div
                        v-for="(line, idx) in kitchenReceiptPreview"
                        :key="idx"
                        :class="[
                          'kitchen-receipt-preview__line',
                          idx === 0
                            ? 'font-weight-bold'
                            : 'grey--text text--darken-1',
                        ]"
                      >
                        <template v-if="idx === 0"
                          >2x&nbsp;&nbsp;{{ line }}</template
                        >
                        <template v-else
                          >&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{{ line }}</template
                        >
                      </div>
                    </div>
                  </template>

                  <div class="mt-4">
                    <TTextField
                      label="footer_text"
                      v-model="store.footer_text"
                      outlined
                      dense
                    />
                  </div>
                </SettingsSectionCard>

                <!-- What to Print Section -->
                <SettingsSectionCard
                  icon="fal fa-print"
                  :title="$t('what_to_print')"
                  :subtitle="`${receiptOnCount} / ${receiptToggleTotal}`"
                  accent
                  class="mb-6"
                  body-class="pa-0"
                >
                  <v-row no-gutters>
                    <v-col
                      v-for="item in visibleReceiptToggleItems"
                      :key="item.key"
                      cols="12"
                      sm="6"
                    >
                      <v-list-item class="border-bottom">
                        <v-list-item-content>
                          <v-list-item-title
                            class="font-weight-medium text-body-2"
                          >
                            {{ $t(item.label) }}
                          </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-switch
                            v-model="store[item.key]"
                            inset
                            hide-details
                            dense
                            color="primary"
                            class="mt-0"
                          />
                        </v-list-item-action>
                      </v-list-item>
                    </v-col>
                  </v-row>
                </SettingsSectionCard>
              </v-col>

              <v-col cols="12" md="5">
                <SettingsSectionCard
                  icon="fal fa-eye"
                  :title="$t('preview')"
                  accent
                  class="settings-preview-card"
                >
                  <template v-if="receiptSize === 'Buffet'">
                    <AppBuffetReceipt
                      :item="buffetPreviewItem"
                      :show_logo="!!store.show_logo"
                      :branch="!!store.branch"
                      :show_phone_number="!!store.show_phone_number"
                      :show_mobile_number="!!store.show_mobile_number"
                      :show_store_address="!!store.show_store_address"
                      :show_payment_method="!!store.show_payment_method"
                      :footer_text="store.footer_text"
                      :outlined="true"
                      :qr_url="demoQrUrl"
                    />
                  </template>
                  <template
                    v-else-if="receiptSize === 'A4-01' || receiptSize === 'A4'"
                  >
                    <AppReceiptA4
                      :item="previewItem"
                      :show_logo="!!store.show_logo"
                      :branch="!!store.branch"
                      :customer_point="!!store.customer_point"
                      :customer_phone="!!store.customer_phone"
                      :customer_address="!!store.customer_address"
                      :customer_logistics="!!store.customer_logistics"
                      :thb_currency="!!store.thb_currency"
                      :tax_include="!!store.tax_include"
                      :usd_currency="!!store.usd_currency"
                      :show_qr="!!store.show_qr"
                      :footer_text="store.footer_text"
                      :show_payment_method="!!store.show_payment_method"
                      :outlined="true"
                      :show_phone_number="!!store.show_phone_number"
                      :show_mobile_number="!!store.show_mobile_number"
                      :show_store_address="!!store.show_store_address"
                      :show_email="!!store.show_email"
                      :show_bank_account="!!store.show_bank_account"
                      :show_amount_letter="!!store.show_amount_letter"
                    />
                  </template>
                  <template v-else-if="receiptSize === 'A4-02'">
                    <AppReceiptA4_02
                      :item="previewItem"
                      :show_logo="!!store.show_logo"
                      :branch="!!store.branch"
                      :customer_point="!!store.customer_point"
                      :customer_phone="!!store.customer_phone"
                      :customer_address="!!store.customer_address"
                      :customer_logistics="!!store.customer_logistics"
                      :thb_currency="!!store.thb_currency"
                      :tax_include="!!store.tax_include"
                      :usd_currency="!!store.usd_currency"
                      :show_qr="!!store.show_qr"
                      :footer_text="store.footer_text"
                      :show_payment_method="!!store.show_payment_method"
                      :outlined="true"
                      :show_phone_number="!!store.show_phone_number"
                      :show_mobile_number="!!store.show_mobile_number"
                      :show_store_address="!!store.show_store_address"
                      :show_email="!!store.show_email"
                      :show_bank_account="!!store.show_bank_account"
                      :show_amount_letter="!!store.show_amount_letter"
                    />
                  </template>
                  <template v-else>
                    <AppReceipt
                      :item="previewItem"
                      :show_logo="!!store.show_logo"
                      :branch="!!store.branch"
                      :customer_point="!!store.customer_point"
                      :customer_phone="!!store.customer_phone"
                      :customer_address="!!store.customer_address"
                      :customer_logistics="!!store.customer_logistics"
                      :thb_currency="!!store.thb_currency"
                      :tax_include="!!store.tax_include"
                      :usd_currency="!!store.usd_currency"
                      :barcode="!!store.barcode"
                      :show_qr="!!store.show_qr"
                      :footer_text="store.footer_text"
                      :receipt_size="receiptSize"
                      :outlined="true"
                      :show_payment_method="!!store.show_payment_method"
                      :show_phone_number="!!store.show_phone_number"
                      :show_mobile_number="!!store.show_mobile_number"
                      :show_store_address="!!store.show_store_address"
                      :receipt_show_stamp_card="!!store.receipt_show_stamp_card"
                    />
                  </template>

                  <div class="d-flex justify-center mt-4">
                    <v-btn
                      color="primary"
                      depressed
                      outlined
                      class="px-4"
                      @click="printBill"
                    >
                      <v-icon small left> fal fa-print </v-icon>
                      {{ $t("print") }}
                    </v-btn>
                  </div>
                </SettingsSectionCard>
              </v-col>
            </v-row>
          </div>
        </v-fade-transition>
      </v-tab-item>

      <!-- Features -->
      <v-tab-item>
        <v-fade-transition hide-on-leave>
          <div class="store-settings-tab-panel py-2">
            <!-- Sales Section -->
            <SettingsSectionCard
              icon="fal fa-bolt"
              :title="$t('sales')"
              accent
              class="mb-6"
              body-class="pa-0"
            >
              <v-list class="py-0">
                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-ticket-alt</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("ticket_verification") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("ticket_verification_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.ticket_verification"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-list-ol</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("enable_bill_queue") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("enable_bill_queue_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.enable_bill_queue"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item
                  v-if="store.store_type_code !== 'POS'"
                  :class="{
                    'border-bottom': store.store_type_code === 'BUFFET',
                  }"
                >
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-globe</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("online_order") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("online_order_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.online_order"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item
                  v-if="store.store_type_code === 'BUFFET'"
                  class="border-bottom"
                >
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-door-open</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("allow_customer_open_table") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("allow_customer_open_table_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.allow_customer_open_table"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item v-if="store.store_type_code === 'BUFFET'">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-qrcode</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("qr_outside_buffet") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("qr_outside_buffet_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.show_qr_outside_buffet_plan"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </SettingsSectionCard>

            <!-- Inventory Section -->
            <SettingsSectionCard
              icon="fal fa-cubes"
              :title="$t('inventory')"
              accent
              class="mb-6"
              body-class="pa-0"
            >
              <v-list class="py-0">
                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <i
                        class="fas fa-recycle primary--text"
                        style="font-size: 1rem"
                      ></i>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("product_soft_delete") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("product_soft_delete_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.product_soft_delete"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item>
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-user-lock</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("staff_delete_own_products") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("staff_delete_own_products_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.can_delete_his_product"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </SettingsSectionCard>

            <!-- Reporting & Security Section -->
            <SettingsSectionCard
              icon="fal fa-shield-alt"
              :title="$t('reporting_security')"
              accent
              class="mb-6"
              body-class="pa-0"
            >
              <v-list class="py-0">
                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-chart-bar</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("advanced_report") }}
                      <v-chip x-small label class="ml-2 rounded-lg">{{
                        planTag
                      }}</v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("advanced_report_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.advanced_report"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-search</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("advanced_search") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("advanced_search_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.advanced_search"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-history</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("activity_log") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("activity_log_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.enable_activity"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-lock</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("pin_protection") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("pin_protection_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.is_protect"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item>
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-random</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("random_transaction_code") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("random_transaction_code_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.is_random_txn_code"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </SettingsSectionCard>

            <!-- Display & Shortcuts Section -->
            <SettingsSectionCard
              icon="fal fa-th"
              :title="$t('display_settings')"
              :subtitle="$t('display_settings_hint')"
              accent
              class="mb-6"
            >
              <div class="text-caption font-weight-medium mb-3">
                {{ $t("cards_per_row") }}
              </div>
              <v-chip-group
                v-model="cardsPerRow"
                mandatory
                active-class="primary--text"
              >
                <v-chip
                  v-for="option in cardsPerRowOptions"
                  :key="option.value"
                  :value="option.value"
                  filter
                  outlined
                  class="rounded-lg"
                >
                  {{ option.label }}
                </v-chip>
              </v-chip-group>
            </SettingsSectionCard>

            <SettingsSectionCard
              icon="fal fa-bolt"
              :title="$t('quick_access')"
              :subtitle="$t('quick_access_hint')"
              accent
              class="mb-6"
            >
              <v-row>
                <v-col
                  v-for="shortcut in availableShortcuts"
                  :key="shortcut.id"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-card
                    class="shortcut-card"
                    @click="navigateTo(shortcut)"
                    hover
                  >
                    <v-card-text class="text-center pa-4">
                      <v-icon size="40" color="primary" class="mb-2">
                        {{ shortcut.icon }}
                      </v-icon>
                      <div class="text-subtitle-2 font-weight-bold mb-1">
                        {{ $t(shortcut.title) }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ $t(shortcut.description) }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </SettingsSectionCard>
          </div>
        </v-fade-transition>
      </v-tab-item>

      <!-- Customer -->
      <v-tab-item>
        <v-fade-transition hide-on-leave>
          <div class="store-settings-tab-panel py-2">
            <!-- Loyalty Points Section -->
            <SettingsSectionCard
              icon="fal fa-gift"
              :title="$t('loyalty_points')"
              :subtitle="$t('loyalty_points_hint')"
              accent
              class="mb-6"
            >
              <v-list-item class="settings-toggle-row mb-2">
                <v-list-item-icon class="mr-3">
                  <v-sheet
                    color="grey lighten-4"
                    rounded="lg"
                    class="d-flex align-center justify-center"
                    width="36"
                    height="36"
                  >
                    <v-icon color="primary" small>fas fa-gift</v-icon>
                  </v-sheet>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ $t("collect_points") }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-switch
                    v-model="store.customer_point"
                    inset
                    hide-details
                    color="primary"
                    class="mt-0"
                  />
                </v-list-item-action>
              </v-list-item>

              <v-expand-transition>
                <v-row v-if="store.customer_point">
                  <v-col cols="12" md="6">
                    <CurrencyInput
                      v-model="store.price_per_point"
                      label="price_per_point"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
              </v-expand-transition>

              <div v-if="isRetailPosStore" class="pa-4 border-top">
                <div class="text-caption font-weight-medium mb-2">
                  {{ $t("refund_order") }}
                </div>
                <v-chip-group
                  v-model="store.return_day"
                  mandatory
                  active-class="primary--text"
                >
                  <v-chip
                    v-for="day in returnDays"
                    :key="day.value"
                    :value="day.value"
                    filter
                    outlined
                    small
                    class="chip-selectable rounded-lg"
                  >
                    {{ day.text }}
                  </v-chip>
                </v-chip-group>
                <div class="text-caption grey--text mt-1">
                  {{ $t("refund_order_hint") }}
                </div>
              </div>
            </SettingsSectionCard>

            <!-- Customer Fields at Checkout -->
            <SettingsSectionCard
              icon="fal fa-user-check"
              :title="$t('customer_fields_checkout')"
              accent
              class="mb-6"
              body-class="pa-0"
            >
              <v-list class="py-0">
                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-phone</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("customer_phone") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("ask_phone_checkout_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.customer_phone"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item class="border-bottom">
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small
                        >fas fa-map-marker-alt</v-icon
                      >
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("customer_address") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("needed_for_delivery_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.customer_address"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>

                <v-list-item>
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small>fas fa-truck</v-icon>
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("logistics") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("choose_courier_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.customer_logistics"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </SettingsSectionCard>

            <!-- Location Lock (Restaurant Only) -->
            <SettingsSectionCard
              v-if="isRestaurant"
              icon="fal fa-map-marker-check"
              :title="$t('location_lock')"
              accent
              class="mb-6"
              body-class="pa-0"
            >
              <v-list class="py-0">
                <v-list-item
                  :class="{ 'border-bottom': store.require_location }"
                >
                  <v-list-item-icon class="mr-3">
                    <v-sheet
                      color="grey lighten-4"
                      rounded="lg"
                      class="d-flex align-center justify-center"
                      width="36"
                      height="36"
                    >
                      <v-icon color="primary" small
                        >fas fa-map-marker-check</v-icon
                      >
                    </v-sheet>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ $t("require_location_check") }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ $t("require_location_check_hint") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-switch
                      v-model="store.require_location"
                      inset
                      hide-details
                      color="primary"
                      class="mt-0"
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>

              <v-expand-transition>
                <div
                  v-if="store.require_location"
                  class="pa-4 location-fields-section"
                >
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <TTextField
                        label="lat"
                        v-model="store.lat"
                        outlined
                        dense
                      >
                        <template v-slot:append>
                          <v-btn
                            small
                            icon
                            @click="getLocation"
                            color="primary"
                          >
                            <v-icon small>fas fa-crosshairs</v-icon>
                          </v-btn>
                        </template>
                      </TTextField>
                    </v-col>
                    <v-col cols="12" md="4">
                      <TTextField
                        label="lng"
                        v-model="store.lng"
                        outlined
                        dense
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <CurrencyInput
                        label="radius"
                        v-model="store.radius"
                        outlined
                        dense
                        suffix="m"
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>

              <v-expand-transition>
                <v-list class="py-0" v-if="store.require_location">
                  <v-list-item class="border-top">
                    <v-list-item-icon class="mr-3">
                      <v-sheet
                        color="grey lighten-4"
                        rounded="lg"
                        class="d-flex align-center justify-center"
                        width="36"
                        height="36"
                      >
                        <v-icon color="primary" small
                          >fas fa-map-marker-alt</v-icon
                        >
                      </v-sheet>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        {{ $t("restaurant_order_radius") }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ $t("restaurant_order_radius_hint") }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <TTextField
                        v-model.number="store.restaurant_radius"
                        typeShow="number"
                        :min="0"
                        :max="100"
                        :suffix="$t('km')"
                        dense
                        style="width: 100px"
                        class="mt-0"
                      />
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-expand-transition>
            </SettingsSectionCard>

            <!-- Stamp Card (Retail POS Only) -->
            <SettingsSectionCard
              v-if="isRetailPosStore"
              icon="fal fa-stamp"
              :title="$t('stamp_card_settings')"
              :subtitle="$t('stamp_card_hint')"
              accent
              class="mb-6"
            >
              <v-row>
                <v-col cols="12">
                  <v-switch
                    v-model="stampConfig.is_active"
                    inset
                    hide-details
                    color="primary"
                    :label="$t('stamp_program_active')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <TTextField
                    v-model.number="stampConfig.stamps_per_purchase"
                    :label="$t('stamps_per_purchase')"
                    type="number"
                    min="1"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <TTextField
                    v-model.number="stampConfig.stamps_required_to_redeem"
                    :label="$t('stamps_to_free_item')"
                    type="number"
                    min="1"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12">
                  <TTextField
                    v-model="stampConfig.program_label"
                    :label="$t('stamp_program_label')"
                    outlined
                    dense
                    :maxlength="120"
                    counter
                    clearable
                  />
                </v-col>
                <v-col cols="12">
                  <v-radio-group
                    v-model="stampConfig.stamp_earn_mode"
                    :label="$t('stamp_earn_mode')"
                    row
                    dense
                    hide-details="auto"
                    class="mt-0 mb-1"
                  >
                    <v-radio
                      :label="$t('stamp_earn_mode_item_qty')"
                      value="item_qty"
                    />
                    <v-radio
                      :label="$t('stamp_earn_mode_ticket')"
                      value="ticket"
                    />
                  </v-radio-group>
                  <p class="text-caption grey--text mb-3">
                    {{ $t("stamp_earn_mode_hint") }}
                  </p>
                </v-col>
                <v-col cols="12">
                  <p class="text-caption grey--text mb-3">
                    {{ $t("stamp_program_any_product_hint") }}
                  </p>
                  <p class="text-caption grey--text mb-2">
                    {{ $t("stamp_save_secondary_hint") }}
                  </p>
                  <v-btn
                    color="primary"
                    depressed
                    :loading="stampSaving"
                    @click="saveStampConfig"
                  >
                    {{ $t("save") }}
                  </v-btn>
                </v-col>
              </v-row>
            </SettingsSectionCard>
          </div>
        </v-fade-transition>
      </v-tab-item>
    </v-tabs-items>

    <!-- Save Bar -->
    <div class="settings-footer elevation-2">
      <span
        class="settings-footer__dot"
        :class="{ 'settings-footer__dot--dirty': isDirty }"
      ></span>
      <div class="settings-footer__text">
        <span class="settings-footer__title">
          {{ isDirty ? $t("unsaved_changes") : $t("all_changes_saved") }}
        </span>
        <span class="settings-footer__note">
          {{
            isDirty
              ? $t("review_changes_hint")
              : `${$t("last_saved")} ${lastSavedNote}`
          }}
        </span>
      </div>
      <v-spacer />
      <v-btn text :disabled="!isDirty" @click="discardChanges" class="mr-2">
        {{ $t("discard") }}
      </v-btn>
      <v-btn
        @click="updateStore()"
        depressed
        color="primary"
        large
        class="px-8 rounded-lg store-settings-save-btn"
        :loading="saving"
        :disabled="!isDirty"
      >
        <v-icon left small>fas fa-save</v-icon>
        {{ $t("save_changes") }}
      </v-btn>
    </div>

    <!-- Weighted Average Calculation Dialog -->
    <app-custom-dialog
      v-model="showDetailedCalculation"
      width="750px"
      :footer="false"
      title="detailed_weighted_average_calculation"
    >
      <div slot="activator">
        <WeightedAverageInfo />
      </div>
    </app-custom-dialog>

    <!-- Customer Screen Settings Dialog -->
    <app-custom-dialog
      v-model="showCustomerScreenDialog"
      title="customer_screen_settings"
      titleIcon="fal fa-tv"
      submitText="save"
      cancelText="cancel"
      width="540px"
      @submit="saveCustomerScreenSettings"
    >
      <div slot="activator">
        <!-- Auto-open on startup -->
        <v-card
          flat
          rounded="lg"
          :color="$vuetify.theme.dark ? 'rgba(76,175,80,0.10)' : '#f0fdf4'"
          :style="{
            border: `1.5px solid ${
              customerScreenSettings.autoOpenOnStartup
                ? '#4caf50'
                : $vuetify.theme.dark
                ? 'rgba(76,175,80,0.25)'
                : 'rgba(76,175,80,0.35)'
            }`,
          }"
          class="mb-5"
        >
          <v-card-text class="pa-3">
            <v-row align="center" no-gutters>
              <v-col cols="auto" class="mr-3">
                <v-avatar
                  size="42"
                  :color="
                    customerScreenSettings.autoOpenOnStartup
                      ? 'success'
                      : $vuetify.theme.dark
                      ? 'grey darken-3'
                      : 'grey lighten-3'
                  "
                >
                  <v-icon
                    small
                    :color="
                      customerScreenSettings.autoOpenOnStartup
                        ? 'white'
                        : 'grey'
                    "
                  >
                    fal fa-desktop
                  </v-icon>
                </v-avatar>
              </v-col>
              <v-col>
                <div class="font-weight-bold body-2">
                  {{ $t("auto_open_on_startup") || "Auto-open on startup" }}
                </div>
                <div
                  class="caption"
                  :class="
                    $vuetify.theme.dark
                      ? 'grey--text text--lighten-1'
                      : 'grey--text'
                  "
                >
                  {{
                    $t("auto_open_startup_desc") ||
                    "Opens when a second monitor is detected on app load"
                  }}
                </div>
              </v-col>
              <v-col cols="auto">
                <v-switch
                  v-model="customerScreenSettings.autoOpenOnStartup"
                  color="success"
                  dense
                  inset
                  hide-details
                  class="mt-0 pt-0"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Position selector -->
        <div
          class="text-caption font-weight-bold grey--text mb-1 text-uppercase"
        >
          {{ $t("secondary_screen_position") || "Secondary Screen Position" }}
        </div>
        <div class="caption grey--text mb-3">
          {{
            $t("secondary_screen_position_desc") ||
            "Where is your iPad / 2nd screen relative to your main monitor?"
          }}
        </div>
        <v-btn-toggle
          v-model="customerScreenSettings.position"
          mandatory
          color="primary"
          class="cs-position-toggle mb-4"
        >
          <v-btn
            v-for="pos in positionOptions"
            :key="pos.value"
            :value="pos.value"
            text
            height="56"
          >
            <div class="d-flex flex-column align-center">
              <v-icon small class="mb-1">{{ pos.icon }}</v-icon>
              <span class="caption text-none">{{ pos.label }}</span>
            </div>
          </v-btn>
        </v-btn-toggle>

        <v-divider class="mb-4" />

        <!-- Monitor index -->
        <TextSelect
          v-model="customerScreenSettings.monitorIndex"
          label="customer_monitor"
          :items="monitorOptions"
          text="text"
          itemValue="value"
          :returnObject="false"
          :searchable="false"
          :placeholder="false"
          class="mb-1"
        />
        <div class="caption grey--text mb-4 ml-1">
          {{
            $t("customer_monitor_hint") ||
            "Select which display shows the customer-facing screen"
          }}
        </div>

        <v-divider class="mb-3" />

        <!-- Checkboxes -->
        <v-checkbox
          v-model="customerScreenSettings.fullscreen"
          :label="$t('fullscreen_mode') || 'Open in fullscreen mode'"
          color="primary"
          hide-details
          dense
          class="mt-1"
        />
        <v-checkbox
          v-model="customerScreenSettings.autoDetect"
          :label="$t('auto_detect_monitor') || 'Auto-detect secondary monitor'"
          color="primary"
          hide-details
          dense
          class="mt-1"
        />
        <v-checkbox
          v-model="customerScreenSettings.showInstructions"
          :label="$t('show_help_instructions') || 'Show help instructions'"
          color="primary"
          hide-details
          dense
          class="mt-1 mb-1"
        />
      </div>
    </app-custom-dialog>
  </div>
</template>

<script>
import TTextField from "@/components/global/TextField";
import TextArea from "@/components/global/TextArea";
import { mapGetters } from "vuex";
import VAvatarUploader from "@/components/VAvatarUploader";
import { setUser } from "@/controllers/auth";
import { success, confirm, error } from "@/controllers/alert";
import { uploadErrorMessage } from "@/utils/uploadError";
import CurrencyInput from "@/components/CurrencyInput";
import { myMixin } from "@/mixin/main";
import TextSelect from "@/components/global/TextSelect";
import WeightedAverageInfo from "@/components/products/WeightedAverageInfo";
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import customerScreenMonitor from "@/utils/customerScreenMonitor";
import SettingsSectionCard from "@/components/settings/SettingsSectionCard.vue";
import { billPOS, buffetBillPOS } from "@/utils/mock";
import AppReceipt from "@/components/settings/bills/AppReceipt";
import AppReceiptA4 from "@/components/settings/bills/AppReceiptA4";
import AppReceiptA4_02 from "@/components/settings/bills/AppReceiptA4_02";
import AppBuffetReceipt from "@/components/settings/bills/AppBuffetReceipt";

export default {
  mixins: [myMixin],
  components: {
    TTextField,
    TextArea,
    VAvatarUploader,
    CurrencyInput,
    TextSelect,
    WeightedAverageInfo,
    DragDropUpload,
    SettingsSectionCard,
    AppReceipt,
    AppReceiptA4,
    AppReceiptA4_02,
    AppBuffetReceipt,
  },
  data() {
    return {
      coverFile: null,
      store: {
        price_per_point: 0,
        online_order: 0,
        advanced_report: 0,
        is_cod: 0,
        ticket_verification: 0,
        require_shift: 0,
        allow_leftover_items: 0,
        leftover_expiration_days: 1,
        require_location: 0,
        restaurant_radius: 0,
        allow_customer_open_table: 0,
        kitchen_receipt_language_mode: "la",
        enable_bill_queue: 0,
        tax: 10,
        tax_include: 0,
        round_up: 0,
        is_protect: 0,
        can_delete_his_product: 0,
        enable_activity: 0,
        show_qr_outside_buffet_plan: 0,
        advanced_search: 0,
        is_random_txn_code: 0,
        show_logo: 1,
        branch: 1,
        show_phone_number: 1,
        show_mobile_number: 1,
        show_store_address: 1,
        show_email: 1,
        show_bank_account: 1,
        show_amount_letter: 1,
        barcode: 1,
        show_qr: 1,
        receipt_show_stamp_card: 0,
        footer_text: "",
        thb_currency: 1,
        usd_currency: 1,
        show_payment_method: 0,
        customer_point: 0,
        customer_phone: 0,
        customer_address: 0,
        customer_logistics: 0,
      },
      receiptSize: "80mm",
      receiptSizeOptions: ["58mm", "80mm", "A4-01", "A4-02", "Buffet"],
      receiptItem: billPOS(),
      buffetItem: buffetBillPOS(),
      paymentMethods: {
        cash: true,
        transfer: true,
        qr: true,
        cash_with_transfer: true,
        cod: true,
      },
      cardsPerRow: 4,
      cardsPerRowOptions: [
        { value: 3, label: "3" },
        { value: 4, label: "4" },
        { value: 5, label: "5" },
      ],
      shortcuts: [
        {
          id: "kitchen",
          title: "kitchen",
          description: "kitchen_description",
          icon: "fas fa-utensils",
          color: "primary",
          route: "/kitchen",
          showFor: ["restaurant"],
        },
        {
          id: "customer_screen",
          title: "customer_screen",
          description: "customer_screen_description",
          icon: "fas fa-tv",
          color: "success",
          route: "/customer-screen",
          showFor: ["all"],
        },
        {
          id: "price_checker",
          title: "price_checker",
          description: "price_checker_description",
          icon: "fas fa-file-invoice-dollar",
          color: "info",
          route: "/price-checker",
          showFor: ["pos"],
        },
        {
          id: "customer_screen_settings",
          title: "customer_screen_settings",
          description: "customer_screen_settings_description",
          icon: "fas fa-cog",
          color: "warning",
          route: null,
          showFor: ["all"],
          action: "configureCustomerScreen",
        },
      ],
      showDetailedCalculation: false,
      showCustomerScreenDialog: false,
      customerScreenSettings: {
        autoOpenOnStartup: false,
        position: "right",
        monitorIndex: 1,
        fullscreen: true,
        autoDetect: true,
        showInstructions: true,
      },
      detectedMonitors: [],
      saving: false,
      stampConfig: {
        stamps_per_purchase: 1,
        stamp_earn_mode: "item_qty",
        stamps_required_to_redeem: 10,
        is_active: true,
        program_label: "",
      },
      stampSaving: false,
      storeSettingsTab: 0,
      baseline: null,
      // `scope` mirrors bills/bill.vue's visibility rules: "a4" only applies to
      // A4-01/A4-02, "thermal" only to 58mm/80mm, undefined = always shown.
      receiptToggleItems: [
        { key: "show_logo", label: "show_logo" },
        { key: "branch", label: "branch" },
        { key: "show_phone_number", label: "receipt_show_store_phone" },
        { key: "show_mobile_number", label: "receipt_show_store_mobile" },
        { key: "show_store_address", label: "receipt_show_store_address" },
        { key: "show_email", label: "show_email", scope: "a4" },
        { key: "show_bank_account", label: "show_bank_account", scope: "a4" },
        { key: "show_amount_letter", label: "show_amount_letter", scope: "a4" },
        { key: "barcode", label: "barcode", scope: "thermal" },
        { key: "show_qr", label: "qr", scope: "thermal" },
        {
          key: "receipt_show_stamp_card",
          label: "receipt_show_stamp_card",
          scope: "thermal",
        },
      ],
    };
  },
  mounted() {
    this.store = { ...this.user.stores };
    this.coverFile = this.urlCover || null;
    this.receiptSize = this._normalizeReceiptSize(
      this.user.branch_id?.receipt_size,
    );
    if (this.isRetailPosStore) {
      this.loadStampConfig();
    }

    // Initialize payment methods from store settings
    if (this.user.stores && this.user.stores.payment_methods) {
      // Parse payment_methods if it's a JSON string, otherwise use as-is
      let paymentMethodsData = this.user.stores.payment_methods;
      if (typeof paymentMethodsData === "string") {
        try {
          paymentMethodsData = JSON.parse(paymentMethodsData);
        } catch (e) {
          console.error("Failed to parse payment_methods:", e);
          paymentMethodsData = {};
        }
      }
      this.paymentMethods = {
        ...this.defaultPaymentMethods,
        ...paymentMethodsData,
      };

      // Auto-disable transfer if QR is active (cash_with_transfer remains available)
      if (this.store.is_active_qr === 1 || this.store.is_active_qr === true) {
        this.paymentMethods.transfer = false;
      }
    }
    // Initialize cardsPerRow from localStorage
    const savedCardsPerRow = localStorage.getItem("saleCardsPerRow");
    if (savedCardsPerRow) {
      this.cardsPerRow = parseInt(savedCardsPerRow, 10);
    }

    this.setBaseline();
  },
  watch: {
    cardsPerRow(newVal) {
      localStorage.setItem("saleCardsPerRow", newVal.toString());
    },
    "store.is_active_qr": {
      handler(newVal) {
        // When QR is enabled, automatically disable transfer only
        // Note: cash_with_transfer remains available
        if (newVal === 1 || newVal === true) {
          this.paymentMethods.transfer = false;
        }
      },
      immediate: false,
    },
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    urlLogo() {
      if (this.user && this.user.stores && this.user.stores.logo) {
        if (
          this.user.stores.logo.includes("http") ||
          this.user.stores.logo.includes("https")
        ) {
          return this.user.stores.logo;
        }
        const baseURL = process.env.VUE_APP_BASE_URL;
        return `${baseURL}/uploads/${this.user.stores.logo}`;
      } else {
        return "/images/logo_square.png";
      }
    },
    urlCover() {
      const cover = this.user?.stores?.cover;
      if (!cover) return "";
      if (cover.includes("http") || cover.includes("https")) {
        return cover;
      }
      const baseURL = process.env.VUE_APP_BASE_URL;
      return `${baseURL}/uploads/${cover}`;
    },
    urlQR() {
      if (this.user && this.user.stores && this.user.stores.qr) {
        if (this.user.stores.qr.includes("com")) {
          return this.user.stores.qr;
        }
        const baseURL = process.env.VUE_APP_BASE_URL;
        return `${baseURL}/uploads/${this.user.stores.qr}`;
      } else {
        return "/images/qrcode.png";
      }
    },
    is_qr() {
      return this.user.stores.qr ? true : false;
    },
    isExpiringSoon() {
      if (!this.user?.stores?.end_date) return false;
      const endDate = new Date(this.user.stores.end_date);
      const today = new Date();
      const diffDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      return diffDays <= 30;
    },
    isRestaurant() {
      return (
        this.store?.store_type_code === "RESTAURANT" ||
        this.user?.stores?.store_type_code === "RESTAURANT"
      );
    },
    isBuffet() {
      return (
        this.store?.store_type_code === "BUFFET" ||
        this.user?.stores?.store_type_code === "BUFFET"
      );
    },
    isRetailPosStore() {
      return this.user?.stores?.store_type_code === "POS";
    },
    hasKitchenReceiptSettings() {
      return this.isRestaurant || this.isBuffet;
    },
    kitchenReceiptLanguageOptions() {
      return [
        { value: "la", label: this.$t("krl_la") },
        { value: "en", label: this.$t("krl_en") },
        { value: "th", label: this.$t("krl_th") },
        { value: "cn", label: this.$t("krl_cn") },
        { value: "la_en", label: this.$t("krl_la_en") },
        { value: "la_th", label: this.$t("krl_la_th") },
        { value: "la_cn", label: this.$t("krl_la_cn") },
        { value: "all", label: this.$t("krl_all") },
      ];
    },
    kitchenReceiptPreview() {
      const sample = {
        la: "ເຂົ້າຜັດ ກະທຽມ",
        en: "Garlic Fried Rice",
        th: "ข้าวผัดกระเทียม",
        cn: "蒜蓉炒饭",
      };
      const mode = this.store?.kitchen_receipt_language_mode || "la";
      switch (mode) {
        case "en":
          return [sample.en];
        case "th":
          return [sample.th];
        case "cn":
          return [sample.cn];
        case "la_en":
          return [sample.la, sample.en];
        case "la_th":
          return [sample.la, sample.th];
        case "la_cn":
          return [sample.la, sample.cn];
        case "all":
          return [sample.la, sample.en, sample.th, sample.cn];
        case "la":
        default:
          return [sample.la];
      }
    },
    returnDays() {
      return [
        { text: this.$t("none_return"), value: 0 },
        { text: "1 " + this.$t("day"), value: 1 },
        { text: "7 " + this.$t("day"), value: 7 },
        { text: "14 " + this.$t("day"), value: 14 },
        { text: "30 " + this.$t("day"), value: 30 },
      ];
    },
    defaultPaymentMethods() {
      return {
        cash: true,
        transfer: true,
        qr: true,
        cash_with_transfer: true,
        cod: true,
      };
    },
    isQRActive() {
      return (
        this.store?.is_active_qr === 1 || this.store?.is_active_qr === true
      );
    },
    paymentMethodsList() {
      const methods = [
        {
          key: "cash",
          label: "cash",
          icon: "fas fa-money-bill",
          color: "green",
          hint: "cash_hint",
        },
      ];

      // Add QR when active
      if (this.isQRActive) {
        methods.push({
          key: "qr",
          label: "qr_code",
          icon: "fas fa-qrcode",
          color: "purple",
          hint: "qr_hint",
        });
      }

      // Cash with Transfer is always shown (regardless of QR)
      methods.push({
        key: "cash_with_transfer",
        label: "cash_with_transfer",
        icon: "fas fa-money-bill-wave",
        color: "teal",
        hint: "cash_with_transfer_hint",
      });

      // Transfer is shown but disabled when QR is active
      methods.push({
        key: "transfer",
        label: "transfer",
        icon: "fas fa-exchange-alt",
        color: "blue",
        hint: "transfer_hint",
        disabled: this.isQRActive, // Disabled when QR is active
      });

      // COD is only shown for POS store type (show in settings so user can enable/disable it)
      const isPOSStore =
        this.store?.store_type_code === "POS" ||
        this.user?.stores?.store_type_code === "POS";
      if (isPOSStore) {
        methods.push({
          key: "cod",
          label: "cod",
          icon: "fas fa-truck",
          color: "orange",
          hint: "cod_hint",
        });
      }

      return methods;
    },
    availableShortcuts() {
      return this.shortcuts.filter((shortcut) => {
        if (shortcut.showFor.includes("all")) {
          return true;
        }
        if (shortcut.showFor.includes("restaurant") && this.isRestaurant) {
          return true;
        }
        if (shortcut.showFor.includes("pos") && this.isRetailPosStore) {
          return true;
        }
        return false;
      });
    },
    positionOptions() {
      return [
        {
          value: "left",
          icon: "fal fa-arrow-left",
          label: this.$t("left") || "Left",
        },
        {
          value: "right",
          icon: "fal fa-arrow-right",
          label: this.$t("right") || "Right",
        },
        {
          value: "above",
          icon: "fal fa-arrow-up",
          label: this.$t("above") || "Above",
        },
        {
          value: "below",
          icon: "fal fa-arrow-down",
          label: this.$t("below") || "Below",
        },
      ];
    },
    monitorOptions() {
      if (this.detectedMonitors.length > 0) {
        return this.detectedMonitors.map((m, i) => ({
          text: `${m.label || `Monitor ${i + 1}`}${
            m.isPrimary ? ` (${this.$t("primary") || "Primary"})` : ""
          } — ${m.width}×${m.height}`,
          value: i,
        }));
      }
      return [
        {
          text: this.$t("primary_monitor") || "Primary Monitor (Main)",
          value: 0,
        },
        {
          text: this.$t("secondary_monitor") || "Secondary Monitor (Customer)",
          value: 1,
        },
      ];
    },
    planTag() {
      const store = this.user?.stores || {};
      const packageName =
        store.package?.name ||
        this.user?.package?.name ||
        store.package_name ||
        store.plan_name;
      return packageName || "-";
    },
    planStats() {
      const s = this.user?.stores || {};
      return [
        {
          label: "product_count",
          value: s.product_count || 0,
          note: "in_catalogue",
        },
        {
          label: "user_count",
          value: s.user_count || 0,
          note: "staff_accounts",
        },
        {
          label: "branch_count",
          value: s.branch_count || 0,
          note: "across_the_store",
        },
        {
          label: "end_date",
          value: s.end_date ? this.fmtDate(s.end_date) : "-",
          note: "subscription_ends",
          isDate: true,
        },
      ];
    },
    planProgressPct() {
      const start = new Date(this.user?.stores?.start_date);
      const end = new Date(this.user?.stores?.end_date);
      const now = new Date();
      if (isNaN(start) || isNaN(end) || end <= start) return 0;
      const pct = ((now - start) / (end - start)) * 100;
      return Math.max(0, Math.min(100, pct));
    },
    planDaysLeft() {
      const end = new Date(this.user?.stores?.end_date);
      const now = new Date();
      if (isNaN(end)) return 0;
      return Math.max(0, Math.round((end - now) / 86400000));
    },
    pmOnCount() {
      return Object.values(this.paymentMethods).filter(Boolean).length;
    },
    visibleReceiptToggleItems() {
      const isA4 = this.receiptSize.includes("A4");
      const isThermal =
        this.receiptSize === "58mm" || this.receiptSize === "80mm";
      return this.receiptToggleItems.filter((i) => {
        if (i.scope === "a4") return isA4;
        if (i.scope === "thermal") return isThermal;
        return true;
      });
    },
    receiptToggleTotal() {
      return this.visibleReceiptToggleItems.length;
    },
    receiptOnCount() {
      return this.visibleReceiptToggleItems.filter((i) => !!this.store[i.key])
        .length;
    },
    demoQrUrl() {
      const origin = window.location.origin;
      const storeId = this.user?.stores?.id || "1";
      const branchId = this.user?.branch_id?.id || "1";
      return `${origin}/scan/${storeId}/${branchId}/1`;
    },
    previewItem() {
      return {
        ...this.receiptItem,
        queue_no: this.store.enable_bill_queue ? 1 : null,
      };
    },
    buffetPreviewItem() {
      return {
        ...this.buffetItem,
        queue_no: this.store.enable_bill_queue ? 1 : null,
      };
    },
    isDirty() {
      if (!this.baseline) return false;
      return JSON.stringify(this._snapshot()) !== JSON.stringify(this.baseline);
    },
    lastSavedNote() {
      const iso = this.user?.stores?.updated_at;
      return iso ? this.fmtDate(iso) : "-";
    },
  },
  methods: {
    rename(file) {
      const ext = file.name.split(".")[1];
      return `1231232.${ext}`;
    },
    async failed(err) {
      console.error("upload failed", err);
      await error(this, uploadErrorMessage(err, this.$t.bind(this)));
    },
    async requestLogo(form, config) {
      const { data } = await this.$http.post("change_store_logo", form, config);
      if (data.status) {
        await setUser(this);
        await success(this, data.message);
      }
    },
    async uploadCover(file) {
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await this.$http.post("change_store_cover", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data.status) {
          await setUser(this);
          this.coverFile = this.urlCover || null;
          await success(this, data.message);
        }
      } catch (err) {
        await error(this, uploadErrorMessage(err, this.$t.bind(this)));
      }
    },
    async conRemove() {
      try {
        const { isConfirmed } = await confirm({
          props: this,
          text: "qr_removed",
          can_txt: "cancel",
          con_txt: "confirm",
        });
        if (isConfirmed) {
          await this.removeQR();
        }
      } catch (error) {
        console.log(error);
      }
    },
    async removeQR() {
      try {
        const { data } = await this.$http.post("remove_qr");
        if (data.status) {
          await setUser(this);
          await success(this, data.message);
        }
      } catch (error) {
        console.log(error);
      }
    },
    async requestQR(form, config) {
      const { data } = await this.$http.post("change_store_qr", form, config);
      if (data.status) {
        await setUser(this);
        await success(this, data.message);
      }
    },
    async loadStampConfig() {
      try {
        const { data } = await this.$http.get("stamp_config");
        if (data && data.status && data.data) {
          const c = data.data;
          this.stampConfig = {
            stamps_per_purchase: c.stamps_per_purchase ?? 1,
            stamp_earn_mode: c.stamp_earn_mode || "item_qty",
            stamps_required_to_redeem: c.stamps_required_to_redeem ?? 10,
            is_active: !!c.is_active,
            program_label: c.program_label ?? "",
          };
        }
      } catch (e) {
        /* optional */
      }
    },
    async saveStampConfig() {
      try {
        this.stampSaving = true;
        const form = {
          stamps_per_purchase: this.stampConfig.stamps_per_purchase,
          stamp_earn_mode: this.stampConfig.stamp_earn_mode || "item_qty",
          stamps_required_to_redeem: this.stampConfig.stamps_required_to_redeem,
          is_active: this.stampConfig.is_active,
          program_label: this.stampConfig.program_label || null,
        };
        const { data } = await this.$http.post("stamp_config", form);
        if (data.status) {
          await success(this, "stamp_config_saved");
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.stampSaving = false;
      }
    },
    _normalizeReceiptSize(size) {
      const val = size ?? "80mm";
      // Handle backward compatibility: convert "A4" to "A4-01"
      return val === "A4" ? "A4-01" : val;
    },
    _snapshot() {
      return {
        store: this.store,
        paymentMethods: this.paymentMethods,
        receiptSize: this.receiptSize,
      };
    },
    setBaseline() {
      this.baseline = JSON.parse(JSON.stringify(this._snapshot()));
    },
    discardChanges() {
      if (!this.baseline) return;
      const snap = JSON.parse(JSON.stringify(this.baseline));
      this.store = snap.store;
      this.paymentMethods = snap.paymentMethods;
      this.receiptSize = snap.receiptSize;
    },
    fmtDate(iso) {
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const d = new Date(iso);
      if (isNaN(d)) return "-";
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    async updateStore() {
      try {
        this.saving = true;
        const storeForm = {
          name: this.store.name,
          mobile_no: this.store.mobile_no,
          address: this.store.address,
          phone_no: this.store.phone_no,
          desc: this.store.desc,
          return_day: this._returnDay(),
          price_per_point: this.store.price_per_point,
          online_order: this.store.online_order,
          currency: this.store.currency,
          tax: this.store.tax,
          tax_include: this.store.tax_include,
          lat: this.store.lat,
          lng: this.store.lng,
          radius: this.store.radius,
          advanced_report: this.store.advanced_report,
          is_cod: this.paymentMethods.cod ? 1 : 0,
          ticket_verification: this.store.ticket_verification,
          require_location: this.store.require_location ? 1 : 0,
          allow_customer_open_table: this.store.allow_customer_open_table
            ? 1
            : 0,
          kitchen_receipt_language_mode:
            this.store.kitchen_receipt_language_mode || "la",
          require_shift: this.store.require_shift,
          is_active_qr: this.store.is_active_qr ? 1 : 0,
          product_soft_delete: this.store.product_soft_delete ? 1 : 0,
          round_up: this.store.round_up ? 1 : 0,
          is_protect: this.store.is_protect ? 1 : 0,
          can_delete_his_product: this.store.can_delete_his_product ? 1 : 0,
          enable_activity: this.store.enable_activity ? 1 : 0,
          show_qr_outside_buffet_plan: this.store.show_qr_outside_buffet_plan
            ? 1
            : 0,
          payment_methods: JSON.stringify(this.paymentMethods),
        };
        const billForm = {
          show_logo: this.store.show_logo,
          branch: this.store.branch,
          show_phone_number: this.store.show_phone_number,
          show_mobile_number: this.store.show_mobile_number,
          show_store_address: this.store.show_store_address,
          customer_point: this.store.customer_point,
          customer_phone: this.store.customer_phone,
          customer_address: this.store.customer_address,
          customer_logistics: this.store.customer_logistics,
          show_payment_method: this.store.show_payment_method,
          thb_currency: this.store.thb_currency,
          usd_currency: this.store.usd_currency,
          barcode: this.store.barcode,
          show_qr: this.store.show_qr,
          footer_text: this.store.footer_text,
          tax_include: this.store.tax_include,
          receipt_size: this.receiptSize,
          show_email: this.store.show_email,
          show_bank_account: this.store.show_bank_account,
          show_amount_letter: this.store.show_amount_letter,
          advanced_search: this.store.advanced_search,
          is_random_txn_code: this.store.is_random_txn_code,
          receipt_show_stamp_card: this.store.receipt_show_stamp_card,
          enable_bill_queue: this.store.enable_bill_queue,
        };
        const [storeRes, billRes] = await Promise.all([
          this.$http.post("store_update", storeForm),
          this.$http.post("store_bill_update", billForm),
        ]);
        if (storeRes.data.status || billRes.data.status) {
          await setUser(this);
          this.setBaseline();
          await success(this, storeRes.data.message || billRes.data.message);
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.saving = false;
      }
    },
    _returnDay() {
      if (this.store.return_day && typeof this.store.return_day === "object") {
        return this.store.return_day.value;
      }
      return this.store.return_day;
    },
    async getLocation() {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
        this.store.lat = position.coords.latitude;
        this.store.lng = position.coords.longitude;
        await success(this, this.$t("location_updated") || "Location updated");
      } catch (error) {
        console.error("Geolocation error:", error);
      }
    },
    printBill() {
      setTimeout(() => {
        this.$htmlToPaper("printBill");
      }, 500);
    },
    // Quick Access Navigation Methods
    navigateTo(shortcut) {
      if (shortcut && shortcut.action === "configureCustomerScreen") {
        this.configureCustomerScreen();
        return;
      }

      if (typeof shortcut === "string") {
        if (shortcut === "/customer-screen") {
          this.openCustomerScreen();
        } else {
          window.open(shortcut, "_blank");
        }
        return;
      }

      if (shortcut && shortcut.route === "/customer-screen") {
        this.openCustomerScreen();
      } else if (shortcut && shortcut.route) {
        window.open(shortcut.route, "_blank");
      }
    },
    openCustomerScreen() {
      try {
        if (!this.checkPopupPermissions()) {
          this.showPopupPermissionWarning();
          return;
        }

        const config = customerScreenMonitor.getCustomerWindowConfig();
        const windowFeatures = this.buildWindowFeatures(config);

        const customerWindow = window.open(
          "/customer-screen",
          "customerScreen",
          windowFeatures,
        );

        if (customerWindow && !customerWindow.closed) {
          setTimeout(() => {
            this.tryFullscreen(customerWindow);
          }, 1000);
        }

        if (!customerWindow || customerWindow.closed) {
          console.warn(
            "Customer screen window was blocked or closed immediately",
          );
          this.showCustomerScreenFallback();
        }
      } catch (error) {
        console.error("Failed to open customer screen:", error);
        if (error.name === "SecurityError" || error.message.includes("popup")) {
          this.showPopupPermissionWarning();
        } else {
          this.showCustomerScreenFallback();
        }
      }
    },
    checkPopupPermissions() {
      try {
        const testWindow = window.open("", "_blank", "width=1,height=1");
        if (testWindow) {
          testWindow.close();
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.warn("Popup permission check failed:", error);
        return false;
      }
    },
    tryFullscreen(window) {
      try {
        if (window.document.documentElement.requestFullscreen) {
          window.document.documentElement.requestFullscreen().catch((err) => {
            console.log("Fullscreen request failed:", err);
          });
        } else if (window.document.documentElement.webkitRequestFullscreen) {
          window.document.documentElement
            .webkitRequestFullscreen()
            .catch((err) => {
              console.log("Webkit fullscreen request failed:", err);
            });
        } else if (window.document.documentElement.mozRequestFullScreen) {
          window.document.documentElement
            .mozRequestFullScreen()
            .catch((err) => {
              console.log("Mozilla fullscreen request failed:", err);
            });
        }
      } catch (error) {
        console.log("Fullscreen attempt failed:", error);
      }
    },
    buildWindowFeatures(monitor) {
      const features = [
        `left=${monitor.x}`,
        `top=${monitor.y}`,
        `width=${monitor.width}`,
        `height=${monitor.height}`,
        "fullscreen=yes",
        "titlebar=no",
        "location=no",
        "menubar=no",
        "scrollbars=no",
        "resizable=no",
        "status=no",
        "toolbar=no",
      ];
      return features.join(",");
    },
    showPopupPermissionWarning() {
      this.$swal
        .fire({
          icon: "warning",
          title:
            this.$t("popup_permission_required") || "Popup Permission Required",
          html: `
          <div style="text-align: left;">
            <p>${
              this.$t("popup_blocked_detailed") ||
              "The browser blocked opening the customer screen. Please allow popups for this site."
            }</p>
            <hr style="margin: 15px 0;">
            <p style="font-size: 14px; color: #666;">
              <strong>${
                this.$t("alternative_method") || "Alternative:"
              }</strong>
              ${
                this.$t("manual_open_alternative") ||
                "You can also open the customer screen manually by typing /customer-screen in the address bar."
              }
            </p>
          </div>
        `,
          confirmButtonText: this.$t("try_again") || "Try Again",
          showCancelButton: true,
          cancelButtonText: this.$t("open_manually") || "Open Manually",
          width: 500,
          customClass: "ps-confirm-swal",
        })
        .then((result) => {
          if (result.isConfirmed) {
            setTimeout(() => this.openCustomerScreen(), 1000);
          } else {
            window.open("/customer-screen", "_blank");
          }
        });
    },
    showCustomerScreenFallback() {
      this.$swal
        .fire({
          icon: "warning",
          title:
            this.$t("customer_screen_blocked") || "Customer Screen Blocked",
          html: `
          <div style="text-align: left;">
            <p>${
              this.$t("popup_blocked_message") ||
              "The browser blocked the automatic opening of the customer screen."
            }</p>
            <p><strong>${
              this.$t("manual_steps") || "Manual Steps:"
            }</strong></p>
            <ol style="margin: 10px 0; padding-left: 20px;">
              <li>${
                this.$t("allow_popups") || "Allow popups for this site"
              }</li>
              <li>${
                this.$t("try_again") ||
                "Try clicking the Customer Screen button again"
              }</li>
              <li>${
                this.$t("or_manual_open") ||
                "Or manually open: /customer-screen"
              }</li>
            </ol>
          </div>
        `,
          confirmButtonText: this.$t("try_again") || "Try Again",
          showCancelButton: true,
          cancelButtonText: this.$t("manual_open") || "Open Manually",
          customClass: "ps-confirm-swal",
        })
        .then((result) => {
          if (result.isConfirmed) {
            setTimeout(() => this.openCustomerScreen(), 500);
          } else {
            window.open("/customer-screen", "_blank");
          }
        });
    },
    async configureCustomerScreen() {
      this.customerScreenSettings = { ...customerScreenMonitor.getConfig() };
      this.detectedMonitors = [];
      this.showCustomerScreenDialog = true;
      // Detect real monitor names in background; TextSelect updates reactively
      try {
        this.detectedMonitors =
          await customerScreenMonitor.detectMonitorsAsync();
      } catch (e) {
        // Falls back to generic labels in monitorOptions computed
      }
    },

    saveCustomerScreenSettings() {
      customerScreenMonitor.saveConfig({ ...this.customerScreenSettings });
      this.showCustomerScreenDialog = false;
      this.$swal.fire({
        icon: "success",
        title: this.$t("settings_saved") || "Settings Saved",
        text:
          this.$t("customer_screen_settings_updated") ||
          "Customer screen settings updated successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    },
  },
};
</script>

<style scoped>
.cs-position-toggle {
  display: flex !important;
  width: 100%;
  border-radius: 10px !important;
  overflow: hidden;
}

.cs-position-toggle .v-btn {
  flex: 1;
  min-width: 0 !important;
  border-radius: 0 !important;
}

.cs-position-toggle .v-btn:first-child {
  border-radius: 10px 0 0 10px !important;
}

.cs-position-toggle .v-btn:last-child {
  border-radius: 0 10px 10px 0 !important;
}

.store-settings {
  --store-settings-surface: #ffffff;
  --store-settings-border: rgba(0, 0, 0, 0.08);
  --store-settings-muted: rgba(0, 0, 0, 0.06);
  --store-settings-row-hover: rgba(0, 0, 0, 0.03);
  max-width: 1080px;
  padding-bottom: 8px;
}

.store-settings .v-list-item__title.font-weight-medium {
  font-size: 13px;
}

.store-settings .v-list-item-icon .v-sheet {
  display: contents !important;
  background: transparent !important;
}

.store-settings .v-list-item-icon .v-icon,
.store-settings .v-list-item-icon .v-icon.primary--text {
  color: #9e9e9e !important;
  font-size: 14px !important;
}

.store-settings-intro {
  padding-bottom: 4px;
  border-bottom: 1px solid var(--store-settings-border);
}

.store-settings-tabs ::v-deep .v-tabs-bar {
  border-bottom: 1px solid var(--store-settings-border);
}

.store-settings-tab-items {
  background: transparent !important;
}

.store-settings-tab-panel {
  min-height: 120px;
}

.settings-footer {
  position: sticky;
  bottom: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 12px 18px;
  margin-top: 8px;
  border-radius: 12px;
  background: var(--store-settings-surface);
  border-top: 1px solid var(--store-settings-border);
  transition: background 0.2s;
}

.settings-footer__dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex: 0 0 8px;
  background: rgba(0, 0, 0, 0.15);
}

.settings-footer__dot--dirty {
  background: var(--v-primary-base, #1976d2);
}

.settings-footer__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.settings-footer__title {
  font-size: 13px;
  font-weight: 500;
}

.settings-footer__note {
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.5);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.store-settings-save-btn {
  min-width: 200px;
}

.store-settings ::v-deep .v-list-item:hover::before,
.store-settings ::v-deep .v-list-item:hover:not(.v-list-item--disabled) {
  background-color: transparent !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.border-top {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.qr-upload-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.v-list-item {
  min-height: 72px;
}

.v-list-item__icon {
  margin-top: 18px;
  margin-bottom: 18px;
}

.v-list-item__action {
  margin-top: 0;
  margin-bottom: 0;
  align-self: center;
}

/* Animation for expandable sections */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}

/* Chip selection styles */
.chip-selectable.v-chip--active {
  border-width: 2px !important;
}

/* Store Info Grid */
.store-info-grid {
  padding: 8px 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.info-label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
}

.info-value {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  text-align: right;
}

/* Plan stat tiles */
.plan-stat-tile {
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid var(--store-settings-border);
}

.plan-stat-tile__label {
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
}

.plan-stat-tile__value {
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.02em;
}

.plan-stat-tile__note {
  font-size: 10.5px;
  color: rgba(0, 0, 0, 0.45);
}

.settings-preview-card ::v-deep .v-card__text {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Shortcut Card Styles — matches the PosSmart v2 product card style
   (products/card.vue): white surface, slate-200 border, 16px radius. */
.shortcut-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px !important;
  box-shadow: none !important;
  transition: border-color 0.15s ease, transform 0.12s ease,
    box-shadow 0.15s ease;
  cursor: pointer;
  height: 100%;
  min-height: 140px;
}

.shortcut-card:hover {
  border-color: #5b39c9;
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.theme--dark .shortcut-card {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.12);
}

.shortcut-card .v-card__text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .store-info-grid {
    padding: 8px;
  }

  .hidden-sm-and-down {
    display: none !important;
  }
}

@media (max-width: 600px) {
  .settings-footer {
    flex-wrap: wrap;
  }

  .store-settings-save-btn {
    width: 100%;
    min-width: 0;
  }
}

/* camera badge: small circle, bottom right, icon centered */
.avatar-camera-badge ::v-deep .v-badge__badge {
  left: auto !important;
  right: 6px !important;
  bottom: 6px !important;
  top: auto !important;
  transform: none;
  min-width: 26px;
  width: 26px;
  height: 26px;
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.avatar-camera-badge ::v-deep .v-badge__badge .v-icon {
  font-size: 12px !important;
  line-height: 1;
  margin: 0;
  color: #fff;
}
</style>

<style>
/* Dark mode support - unscoped to work with parent .theme--dark */
.theme--dark .store-settings {
  --store-settings-surface: #121212;
  --store-settings-border: rgba(255, 255, 255, 0.12);
  --store-settings-muted: rgba(255, 255, 255, 0.08);
  --store-settings-row-hover: rgba(255, 255, 255, 0.06);
}

.theme--dark .store-settings .settings-footer {
  border-top-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .store-settings .settings-footer__note {
  color: rgba(255, 255, 255, 0.6);
}

.theme--dark .store-settings .plan-stat-tile {
  background: rgba(255, 255, 255, 0.05);
}

.theme--dark .store-settings .plan-stat-tile__label,
.theme--dark .store-settings .plan-stat-tile__note {
  color: rgba(255, 255, 255, 0.6);
}

.theme--dark .store-settings .store-settings-tabs .v-tabs-bar {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .store-settings .border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.theme--dark .store-settings .border-top {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.theme--dark .store-settings .info-label {
  color: rgba(255, 255, 255, 0.7);
}

.theme--dark .store-settings .info-value {
  color: rgba(255, 255, 255, 0.87);
}

/* QR Upload Section Dark Mode */
.store-settings .qr-upload-section {
  background-color: #fafafa;
}

.store-settings .qr-avatar-bg {
  background-color: #e0e0e0;
}

.theme--dark .store-settings .qr-upload-section {
  background-color: #2d2d2d;
}

.theme--dark .store-settings .qr-avatar-bg {
  background-color: #424242;
}

/* Location Fields Section Dark Mode */
.store-settings .location-fields-section {
  background-color: #fafafa;
}

.theme--dark .store-settings .location-fields-section {
  background-color: #2d2d2d;
}

/* Kitchen Receipt Preview */
.kitchen-receipt-preview {
  background-color: #fafafa;
  border: 1px dashed rgba(0, 0, 0, 0.12);
  font-family: ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 13px;
  line-height: 1.6;
}

.theme--dark .kitchen-receipt-preview {
  background-color: #1f1f1f;
  border-color: rgba(255, 255, 255, 0.18);
}

.kitchen-receipt-preview__line {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* QR Upload Hint Text Dark Mode */
.store-settings .qr-upload-hint {
  color: rgba(0, 0, 0, 0.6);
}

.theme--dark .store-settings .qr-upload-hint {
  color: rgba(255, 255, 255, 0.7);
}

/* QR Description Text Dark Mode */
.store-settings .qr-description-text {
  color: rgba(0, 0, 0, 0.6);
}

.theme--dark .store-settings .qr-description-text {
  color: rgba(255, 255, 255, 0.7);
}
</style>
